import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { ColumnDataType }
  from 'app/components/favourite-layout/favourite/common/favourite-interfaces';
import { CaseTableSummarySection, DisplayDateValue,
  DisplayResultValue } from 'app/area/case-report/case-table-view/components/case-table-view-cells';
import { CASE_TABLE_VIEW_DATE_FORMAT, getColumnData } from 'app/area/case-report/case-table-view/components/case-table-view-util';
import styles from './../case-table-view.scss';
import { Entity } from 'app/types-shared';
import { CaseTableViewMeatballMenu } from 'app/area/case-report/case-table-view/components/case-table-view-meatball-menu';
import { CASE_NO_COLUMN_NAME } from 'app/area/case-report/case-table-view/constants';
import { caseTableViewExternalUrlHook } from 'app/area/case-report/case-table-view/hooks/case-table-view-external-url-hook';
import { getDomainAttributesForRow } from 'app/area/case-report/case-table-view/map-summary-section-column';
import { CaseTableViewContext } from 'app/area/case-report/case-table-view/case-table-view-context';
import { getUrlOpenCase } from 'app/components/helpers';

export const getCaseIdField = (fields: Entity[]) => {
  const field = fields?.[0]?.[CASE_NO_COLUMN_NAME]?.[0] || null;
  if (field) {
    return field.value;
  }
  return null;
};

export const getCaseType = (entity: Entity) => entity?.caseType?.[0]?.value ?? null;

const TableRowFields = React.memo(({ index, row }: { index: number; row: Entity[] }) => {
  const { columns, locale, domainAttributes, tableViewRef, location, searchParams } = useContext(CaseTableViewContext);
  const caseId = getCaseIdField(row);
  const caseType = getCaseType(row?.[0]);
  const domainAttributesForRow = useMemo(() => getDomainAttributesForRow({ domainAttributes, caseType }), [caseType]);
  const renderCells = useCallback(() => {
    return columns.map((column, columnIndex) => {
      if (column.dataType === ColumnDataType.SummarySection) {
        return (
          <td key={`${index}-${columnIndex}`} className={styles.summarySectionColumn}>
            <CaseTableSummarySection
              componentId={`data-column-${index}-${columnIndex}`}
              column={column}
              row={row}
              caseId={caseId}
              domainAttributesForRow={domainAttributesForRow}
              tableViewRef={tableViewRef}
              locale={locale}
              location={location}
              searchParams={searchParams}
            />
          </td>
        );
      } else {
        const itemTd = getColumnData(row, column);
        if (!itemTd) {
          return <td key={`${index}-${columnIndex}`} className={styles.emptyColumn} />;
        }
        return (
          <td key={`${index}-${columnIndex}`} >
            {column.dataType === ColumnDataType.Date ?
              <DisplayDateValue
                componentId={`data-column-${index}-${columnIndex}`}
                cells={itemTd}
                dateTimeFormatter={CASE_TABLE_VIEW_DATE_FORMAT}
                column={column}
                locale={locale}
                domainAttributes={domainAttributesForRow[column.domain as number]}
              />
              :
              <DisplayResultValue
                componentId={`data-column-${index}-${columnIndex}`}
                cells={itemTd}
                column={column}
                location={location}
                toCaseView={() => getUrlOpenCase(caseId, location.pathname, searchParams)}
              />
            }
          </td>
        );
      }
    });
  }, [row, columns, locale]);

  return (
    <>
      {renderCells()}
    </>
  );
});

export const CaseTableViewRows = () => {
  const { rows, tableContainerRef } = useContext(CaseTableViewContext);
  const { singleSupplierUrl, loadSingleSupplierUrl } = caseTableViewExternalUrlHook();
  useEffect(() => {
    loadSingleSupplierUrl();
  }, [loadSingleSupplierUrl]);

  if (!rows?.length) {
    return null;
  }

  return (
    <div className={styles.caseViewTable} ref={tableContainerRef}>
      <table>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={`row-${rowIndex}`}>
              <TableRowFields key={rowIndex} row={row} index={rowIndex} />
              <td className={styles.contextMenuWrapper}>
                <CaseTableViewMeatballMenu
                  caseId={getCaseIdField(row)}
                  singleSupplierUrl={singleSupplierUrl}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
